import { useCallback, useMemo, useSyncExternalStore } from "react"
import { Alert, Autocomplete, Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { LOADING } from "helpers/data-statuses"
import { IsoDate } from "helpers/types/dates"
import type { OptimizationExperimentSource, OptimizationSourceKind } from "connected-components/optimize-experiments/helpers/get-optimization-position-set"
import { OPTIMIZATION_SOURCE_EXPERIMENT_LABEL, OPTIMIZATION_SOURCE_HINT_PORTFOLIO, OPTIMIZATION_SOURCE_ORIGINAL_LABEL, PORTFOLIO_INPUT_HEADING, PORTFOLIO_INPUT_TOOLTIP_BASE } from "connected-components/optimize-experiments/constants"
import { usePortfolioExperiments } from "../../hooks/use-portfolio-experiments"

import { SourcePanelHeading } from "./source-panel-heading"
import {
  getSaveExperimentRegistration,
  subscribeSaveExperimentRegistration,
} from "connected-components/dashboard/save-experiment-launcher-store"

import "./style.scss"

interface ExperimentOption {
  id: string
  name: string
}

interface OptimizationSourcePanelProps {
  portfolioId: string | undefined
  endDate: IsoDate
  optimizationSourceKind: OptimizationSourceKind
  optimizationExperimentSource: OptimizationExperimentSource | null
  setOptimizationSourceKind: (kind: OptimizationSourceKind) => void
  setOptimizationExperimentSource: (source: OptimizationExperimentSource | null) => void
  tradesCount: number
  isIntradayMode: boolean
  isOptimizing: boolean
}

export default function OptimizationSourcePanel({
  portfolioId,
  endDate,
  optimizationSourceKind,
  optimizationExperimentSource,
  setOptimizationSourceKind,
  setOptimizationExperimentSource,
  tradesCount,
  isIntradayMode,
  isOptimizing,
}: OptimizationSourcePanelProps) {
  const { experiments, status, refetch } = usePortfolioExperiments(portfolioId)

  const controlsDisabled = isIntradayMode || isOptimizing

  const experimentsForDate = useMemo(() => {
    if (!endDate) return experiments
    return experiments.filter((e) => e.availableFrom != null && e.availableFrom <= endDate)
  }, [experiments, endDate])

  /** Keep the current selection in the list when filters disagree (e.g. date changed). */
  const experimentOptions: ExperimentOption[] = useMemo(() => {
    if (
      optimizationExperimentSource
      && !experimentsForDate.some(e => e.id === optimizationExperimentSource.experimentId)
    ) {
      return [...experimentsForDate, { id: optimizationExperimentSource.experimentId, name: optimizationExperimentSource.name }]
    }
    return experimentsForDate
  }, [experimentsForDate, optimizationExperimentSource])

  const selectedExperimentOption = useMemo((): ExperimentOption | null => {
    if (!optimizationExperimentSource) {
      return null
    }
    return experimentOptions.find(e => e.id === optimizationExperimentSource.experimentId) ?? {
      id: optimizationExperimentSource.experimentId,
      name: optimizationExperimentSource.name,
    }
  }, [optimizationExperimentSource, experimentOptions])

  const handleKindChange = useCallback((_event: unknown, value: OptimizationSourceKind | null) => {
    if (!value) return
    setOptimizationSourceKind(value)
  }, [setOptimizationSourceKind])

  const handleExperimentOptionChange = useCallback((_event: unknown, option: ExperimentOption | null) => {
    if (!option) {
      setOptimizationExperimentSource(null)
      return
    }
    setOptimizationExperimentSource({ experimentId: option.id, name: option.name })
  }, [setOptimizationExperimentSource])

  const sourceInputSummary = useMemo(() => {
    if (optimizationSourceKind === "experiment" && optimizationExperimentSource) {
      return optimizationExperimentSource.name
    }
    return OPTIMIZATION_SOURCE_ORIGINAL_LABEL
  }, [optimizationSourceKind, optimizationExperimentSource])

  const showSaveTradesHint = tradesCount > 0 && status !== LOADING

  const saveExperimentRegistration = useSyncExternalStore(
    subscribeSaveExperimentRegistration,
    getSaveExperimentRegistration,
    () => null,
  )

  return (
    <Box
      className="components_optimization-source-panel"
      aria-busy={isOptimizing}
    >
      <SourcePanelHeading
        inputHeading={PORTFOLIO_INPUT_HEADING}
        tooltipBase={PORTFOLIO_INPUT_TOOLTIP_BASE}
        sourceInputSummary={sourceInputSummary}
        interactionDisabled={isOptimizing}
      />
      <Typography variant="caption" color="inherit" component="div" className="source-hint">
        {OPTIMIZATION_SOURCE_HINT_PORTFOLIO}
      </Typography>
      <>
        <ToggleButtonGroup
          exclusive
          value={optimizationSourceKind}
          onChange={handleKindChange}
          size="small"
          className="source-toggle"
          disabled={controlsDisabled}
        >
          <ToggleButton value="original" aria-label="Optimize from original portfolio">
            {OPTIMIZATION_SOURCE_ORIGINAL_LABEL}
          </ToggleButton>
          <ToggleButton value="experiment" aria-label="Optimize from saved experiment">
            {OPTIMIZATION_SOURCE_EXPERIMENT_LABEL}
          </ToggleButton>
        </ToggleButtonGroup>
        {optimizationSourceKind === "experiment" && (
          <div className="experiment-autocomplete">
            <Autocomplete
              size="small"
              options={experimentOptions}
              value={selectedExperimentOption}
              onChange={handleExperimentOptionChange}
              onOpen={refetch}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={controlsDisabled}
              loading={status === LOADING}
              noOptionsText={status === LOADING ? "Loading…" : "No matching experiments"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Experiment"
                  placeholder="Search experiments…"
                />
              )}
            />
          </div>
        )}
        {optimizationSourceKind === "experiment" && experimentOptions.length === 0 && status !== LOADING && (
          <Alert severity="info" className="experiment-empty-alert">
            No saved experiments are available for this portfolio on the analysis date. Save a simulation or upload an experiment first.
          </Alert>
        )}
      </>
      {showSaveTradesHint && (
        <Alert severity="warning" className="save-trades-alert">
          <Box className="save-trades-alert-body">
            <span>
              To optimize from your sketched trades, save them as an experiment, then select it above.
            </span>
            {saveExperimentRegistration && (
              <Button
                type="button"
                variant="outlined"
                size="small"
                className="save-trades-alert-action"
                disabled={saveExperimentRegistration.disabled}
                title={saveExperimentRegistration.tooltip}
                onClick={() => saveExperimentRegistration.open()}
              >
                Save as experiment
              </Button>
            )}
          </Box>
        </Alert>
      )}
    </Box>
  )
}
